import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Project, ProjectFolder, Space } from '@vidmakr/bindings/types';
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  Clapperboard,
  Copy,
  ExternalLink,
  Folder,
  FolderInput,
  FolderOpen,
  FolderPlus,
  Image as ImageIcon,
  LayoutTemplate,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  X,
  MessagesSquare,
} from 'lucide-react';
import { AccessDialog, MoveDialog, NameDialog, SpaceDialog } from '@/components/Spaces';
import { GENERAL, PROJECT_DRAG, moveProjectItems, placeScope, placeUrl, useSpaces, type TargetKind } from '@/lib/spaces';
import { SelectCheck, plural, useDragMove, useSelection } from '@/lib/selection';
import { ImagePicker } from '@/components/ImagePicker';
import { PremiereImportButton } from '@/components/PremiereImport';
import { FPS_OPTIONS, LOGO_SIZE, PROJECT_PRESETS, aspectLabel, formatDuration, latestBy, sortRows, subtreeIds, uuidv7, type SortValue } from '@vidmakr/shared';
import { Avatar, Badge, Button, ConfirmModal, EmptyState, Field, Input, Menu, Modal, PageHeader, Select, Spinner, Tabs, cx, type MenuItem } from '@/components/ui';
import { openContextMenu, type ContextEntry } from '@/components/ContextMenu';
import { InspectorPanel, InspectorToggle, SortTh, ViewToggle, rowTone, tableCls } from '@/components/Browse';
import { ProjectThumb, ProjectsInspector, REVIEW_LABEL } from '@/components/Inspectors';
import { useLogos, useOrg } from '@/lib/hooks';
import { reducers } from '@/lib/stdb';
import { attempt, toast } from '@/lib/toast';
import { formatBytes, timeAgo } from '@/lib/format';
import { useInspector, useSort, useViewMode } from '@/lib/browse';
import { useMediaIndex } from '@/lib/library';

type ProjectRow = Project;

const SORT_KEYS = ['name', 'format', 'fps', 'duration', 'size', 'review', 'createdBy', 'modified'] as const;
type SortKey = (typeof SORT_KEYS)[number];
const REVIEW_ORDER: Record<string, number> = { changes_requested: 1, in_review: 2, approved: 3 };

/**
 * The project browser: the organization's spaces (home), or a space or folder
 * with its subfolders and projects, as a table (the default) or a grid, with a
 * details panel on the right.
 * - Click selects (Shift: range, Ctrl/⌘: toggle); drag on empty space to
 *   select with a box; double-click opens. Ctrl/⌘+A, Esc, Delete and F2 work too.
 * - Drag the selection onto a folder, a breadcrumb, a space card or the sidebar to move it.
 */
export function ProjectsPage() {
  const { orgId, spaceId: spaceParam, folderId = '' } = useParams();
  const navigate = useNavigate();
  const { org, can, ready } = useOrg(orgId);
  const sp = useSpaces(orgId);
  const inSpace = spaceParam !== undefined;
  const spaceId = spaceParam === GENERAL || !spaceParam ? '' : spaceParam;
  const space = spaceId ? sp.spaceById.get(spaceId) : undefined;
  const folder = folderId ? sp.folderById.get(folderId) : undefined;
  const [projects, projectsReady] = useTable(tables.project.where((p) => p.orgId.eq(orgId ?? '')));
  const [presence] = useTable(tables.presence.where((p) => p.orgId.eq(orgId ?? '')));
  const [assetRows] = useTable(tables.asset.where((a) => a.orgId.eq(orgId ?? '')));
  const [usage] = useTable(tables.projectAsset.where((r) => r.orgId.eq(orgId ?? '')));
  const index = useMediaIndex(assetRows, projects, usage);
  const assetMap = index.byId;
  const [filter, setFilter] = useState<'active' | 'templates' | 'archived'>('active');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<ProjectRow | null>(null);
  const [logoFor, setLogoFor] = useState<ProjectRow | null>(null);
  const logos = useLogos();
  /** Selection keys (`p:<project id>`, `f:<folder id>`) being moved or deleted. */
  const [moving, setMoving] = useState<string[] | null>(null);
  const [deleting, setDeleting] = useState<string[] | null>(null);
  const [newFolder, setNewFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<ProjectFolder | null>(null);
  const [newSpace, setNewSpace] = useState(false);
  const [editSpace, setEditSpace] = useState<Space | null>(null);
  const [access, setAccess] = useState<{ kind: TargetKind; id: string; name: string; restricted: boolean } | null>(null);
  const [view, setView] = useViewMode('projects');
  const [sort, onSort] = useSort<SortKey>('projects', SORT_KEYS, { key: 'modified', dir: 'desc' });
  const inspector = useInspector();

  const here = placeScope(sp.spaceById, sp.folderById, spaceId, folderId);
  const canEditHere = inSpace ? sp.canIn(here, 'editor') : can('editor');

  /** Projects (all of them, archived too), media size and newest edit of each folder, counting its subfolders. */
  const folderStats = useMemo(() => {
    const memo = new Map<string, { active: number; size: number; latest?: ProjectRow }>();
    return (id: string) => {
      const done = memo.get(id);
      if (done) return done;
      const tree = subtreeIds(sp.folders, id);
      const inside = projects.filter((p) => tree.has(p.folderId));
      const s = { active: inside.filter((p) => !p.archived).length, size: index.sizeOf(inside.flatMap(index.mediaOf)), latest: latestBy(inside, (p) => p.updatedAt.microsSinceUnixEpoch) };
      memo.set(id, s);
      return s;
    };
  }, [sp.folders, projects, index]);

  const projectValue = (p: ProjectRow, k: SortKey): SortValue =>
    k === 'name'
      ? p.name
      : k === 'format'
        ? p.width / p.height
        : k === 'fps'
          ? p.fps
          : k === 'duration'
            ? p.durationUs
            : k === 'size'
              ? index.sizeOf(index.mediaOf(p))
              : k === 'review'
                ? REVIEW_ORDER[p.reviewStatus]
                : k === 'createdBy'
                  ? p.createdByName
                  : p.updatedAt.microsSinceUnixEpoch;
  const folderValue = (f: ProjectFolder, k: SortKey): SortValue =>
    k === 'size' ? folderStats(f.id).size : k === 'createdBy' ? f.createdByName : k === 'modified' ? (folderStats(f.id).latest?.updatedAt ?? f.createdAt).microsSinceUnixEpoch : f.name;
  const folderSorts = sort.key === 'name' || sort.key === 'size' || sort.key === 'createdBy' || sort.key === 'modified';

  const filtered = useMemo(
    () =>
      sortRows(
        [...projects]
          .filter((p) => (filter === 'archived' ? p.archived : filter === 'templates' ? p.template && !p.archived : !p.archived))
          .filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
          .sort((a, b) => Number(b.updatedAt.microsSinceUnixEpoch - a.updatedAt.microsSinceUnixEpoch)),
        (p) => projectValue(p, sort.key),
        sort.dir,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, filter, query, sort.key, sort.dir, index],
  );
  // In a space: what is in this folder (search looks through the whole space).
  // (The Templates tab lists every template, to start projects from here.)
  const visible = inSpace && filter !== 'templates' ? filtered.filter((p) => p.spaceId === spaceId && (query ? true : p.folderId === folderId)) : filtered;
  const subfolders =
    inSpace && !query
      ? sortRows(
          sp.folders.filter((f) => f.spaceId === spaceId && f.parentId === folderId),
          (f) => (folderSorts ? folderValue(f, sort.key) : f.name),
          folderSorts ? sort.dir : 'asc',
        )
      : [];
  // Folders shared with someone who can't see the space they are in (guests).
  const sharedFolders = !inSpace ? sp.folders.filter((f) => f.restricted && f.spaceId && !sp.spaceById.has(f.spaceId) && !sp.folderById.has(f.parentId)) : [];
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  // ----- selection and moving
  const order = [...subfolders.map((f) => `f:${f.id}`), ...visible.map((p) => `p:${p.id}`)];
  const canEditKey = (k: string) => sp.canIn((k.startsWith('f:') ? sp.folderById.get(k.slice(2))?.scopeId : projectById.get(k.slice(2))?.scopeId) ?? '', 'editor');
  // Deleting a project takes admin rights where it is; a folder, editing rights.
  const canDeleteKey = (k: string) => (k.startsWith('f:') ? canEditKey(k) : sp.canIn(projectById.get(k.slice(2))?.scopeId ?? '', 'admin'));
  const { sel, selected, targetsOf, clear, toggle, setAll, onItemClick, onItemContextMenu, boxHandlers, boxElement, boxing, gridRef } = useSelection(order, {
    resetOn: [spaceId, folderId, filter, query],
    onDelete: (keys) => keys.every(canDeleteKey) && setDeleting(keys),
    onRename: (k) => {
      if (!canEditKey(k)) return;
      if (k.startsWith('f:')) setRenamingFolder(sp.folderById.get(k.slice(2)) ?? null);
      else setRenaming(projectById.get(k.slice(2)) ?? null);
    },
  });
  type Place = { spaceId: string; folderId: string };
  const moveProblem = (keys: string[], dest: Place) => {
    for (const k of keys) {
      if (!k.startsWith('f:') || !dest.folderId) continue;
      for (let f = sp.folderById.get(dest.folderId), i = 0; f && i < 64; f = sp.folderById.get(f.parentId), i++) if (`f:${f.id}` === k) return 'A folder can’t move into itself';
    }
    return '';
  };
  const moveTo = async (keys: string[], dest: Place) => {
    const problem = moveProblem(keys, dest);
    if (problem) return (toast.error(problem), false);
    const changed = keys.filter((k) => {
      if (k.startsWith('p:')) {
        const p = projectById.get(k.slice(2));
        return !!p && (p.spaceId !== dest.spaceId || p.folderId !== dest.folderId);
      }
      const f = sp.folderById.get(k.slice(2));
      return !!f && f.id !== dest.folderId && (f.spaceId !== dest.spaceId || f.parentId !== dest.folderId);
    });
    if (!changed.length) return true;
    const ok = await moveProjectItems(changed, dest);
    if (ok) clear();
    return ok;
  };
  const { dropOn, dragStart, dragEnd, dropTarget } = useDragMove<Place>(PROJECT_DRAG, { problem: moveProblem, onDrop: (keys, dest) => void moveTo(keys, dest) });
  const onCardDragStart = (e: React.DragEvent, k: string) => dragStart(e, sel.has(k) ? selected : [k]);
  const setArchived = (keys: string[], archived: boolean) =>
    attempt(async () => {
      for (const k of keys) if (k.startsWith('p:')) await reducers().updateProject({ projectId: k.slice(2), archived } as any);
    }, `${archived ? 'Archived' : 'Restored'} ${plural(keys.length, 'project')}`).then((ok) => ok && clear());

  if (!ready || !projectsReady) {
    return (
      <div className="flex justify-center py-24 text-muted">
        <Spinner />
      </div>
    );
  }
  if (!org) return <EmptyState title="Organization not found">You may not have access to it anymore.</EmptyState>;
  if (inSpace && spaceId && !space && sp.ready) return <EmptyState title="Space not found">It was deleted, or it isn't shared with you.</EmptyState>;
  if (folderId && !folder && sp.ready) return <EmptyState title="Folder not found">It was deleted, or it isn't shared with you.</EmptyState>;

  // ----- actions. The row/card "…" menu and the right-click menu are built
  // here, once per kind of item: add new per-project actions to projectMenu.
  const moveLabel = (keys: string[]) => (keys.length > 1 ? `Move ${plural(keys.length, 'item')}…` : 'Move to…');
  const deleteLabel = (keys: string[]) => (keys.length > 1 ? `Delete ${plural(keys.length, 'item')}` : keys[0]?.startsWith('f:') ? 'Delete folder' : 'Delete');
  const openFolder = (f: ProjectFolder) => navigate(placeUrl(org.id, f.spaceId, f.id));
  const folderMenu = (f: ProjectFolder): (MenuItem | 'sep')[] =>
    sp.canIn(f.scopeId, 'editor')
      ? [
          { label: 'Open', icon: <FolderOpen size={14} />, onClick: () => openFolder(f) },
          { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setRenamingFolder(f) },
          ...(sp.role !== 'guest' ? [{ label: 'Access…', icon: <Lock size={14} />, onClick: () => setAccess({ kind: 'folder', id: f.id, name: f.name, restricted: f.restricted }) }] : []),
          { label: moveLabel(targetsOf(`f:${f.id}`)), icon: <FolderInput size={14} />, onClick: () => setMoving(targetsOf(`f:${f.id}`)) },
          'sep',
          { label: deleteLabel(targetsOf(`f:${f.id}`)), icon: <Trash2 size={14} />, danger: true, disabled: !targetsOf(`f:${f.id}`).every(canDeleteKey), onClick: () => setDeleting(targetsOf(`f:${f.id}`)) },
        ]
      : [{ label: 'Open', icon: <FolderOpen size={14} />, onClick: () => openFolder(f) }];
  // A template is used where you are browsing (next to it on the overview); viewing it is enough.
  const templatePlace = (p: ProjectRow) => (inSpace ? { spaceId, folderId } : { spaceId: p.spaceId, folderId: p.folderId });
  const canUseTemplate = (p: ProjectRow) => {
    const place = templatePlace(p);
    return p.template && sp.canIn(placeScope(sp.spaceById, sp.folderById, place.spaceId, place.folderId), 'editor');
  };
  const useTemplate = (p: ProjectRow) => {
    const id = uuidv7();
    void attempt(() =>
      reducers().createFromTemplate({ templateId: p.id, newId: id, name: p.name.replace(/\s*template$/i, '') || p.name, ...templatePlace(p) }),
    ).then((ok) => ok && navigate(`/p/${id}`));
  };
  /** The "…" menu of a project the viewer can edit. */
  const projectMenu = (p: ProjectRow): (MenuItem | 'sep')[] => {
    const targets = targetsOf(`p:${p.id}`);
    return [
      ...(canUseTemplate(p) ? [{ label: 'New project from this template', icon: <LayoutTemplate size={14} />, onClick: () => useTemplate(p) }] : []),
      {
        label: p.template ? 'Remove from templates' : 'Make a template',
        icon: <LayoutTemplate size={14} />,
        onClick: () => void attempt(() => reducers().setProjectTemplate({ projectId: p.id, template: !p.template }), p.template ? 'Removed from templates' : 'Listed under Templates'),
      },
      { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setRenaming(p) },
      { label: moveLabel(targets), icon: <FolderInput size={14} />, onClick: () => setMoving(targets) },
      { label: 'Change logo…', icon: <ImageIcon size={14} />, onClick: () => setLogoFor(p) },
      {
        label: 'Duplicate',
        icon: <Copy size={14} />,
        onClick: () => {
          const id = uuidv7();
          void attempt(() => reducers().duplicateProject({ projectId: p.id, newId: id, name: `${p.name} (copy)` }), 'Project duplicated');
        },
      },
      {
        label: p.archived ? 'Restore' : 'Archive',
        icon: p.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />,
        onClick: () => void attempt(() => reducers().updateProject({ projectId: p.id, archived: !p.archived } as any)),
      },
      ...(targets.every(canDeleteKey) ? (['sep', { label: deleteLabel(targets), icon: <Trash2 size={14} />, danger: true, onClick: () => setDeleting(targets) }] as const) : []),
    ];
  };
  const projectContext = (p: ProjectRow): ContextEntry[] => [
    { label: 'Open', icon: <Clapperboard size={14} />, onClick: () => navigate(`/p/${p.id}`) },
    { label: 'Open in new tab', icon: <ExternalLink size={14} />, onClick: () => window.open(`/p/${p.id}`, '_blank') },
    { label: 'Reviews', icon: <MessagesSquare size={14} />, onClick: () => navigate(`/p/${p.id}/reviews`) },
    ...(sp.canIn(p.scopeId, 'editor')
      ? (['sep', ...projectMenu(p)] as const)
      : canUseTemplate(p)
        ? (['sep', { label: 'New project from this template', icon: <LayoutTemplate size={14} />, onClick: () => useTemplate(p) }] as const)
        : []),
  ];

  /** Props every folder card or row shares: selection, dragging, dropping, menus. */
  const folderProps = (f: ProjectFolder) => {
    const k = `f:${f.id}`;
    return {
      tabIndex: 0,
      onClick: (e: React.MouseEvent) => onItemClick(e, k, () => openFolder(f)),
      onDoubleClick: () => openFolder(f),
      onKeyDown: (e: React.KeyboardEvent) => e.key === 'Enter' && e.target === e.currentTarget && openFolder(f),
      onContextMenu: (e: React.MouseEvent) => {
        onItemContextMenu(k);
        openContextMenu(e, folderMenu(f));
      },
      draggable: sp.canIn(f.scopeId, 'editor'),
      onDragStart: (e: React.DragEvent) => onCardDragStart(e, k),
      onDragEnd: dragEnd,
      ...dropTarget(f.id, () => ({ spaceId, folderId: f.id })),
      'data-folder': f.id,
      'data-sel-key': k,
      'data-selected': sel.has(k) || undefined,
    };
  };
  const projectProps = (p: ProjectRow) => {
    const k = `p:${p.id}`;
    return {
      draggable: sp.canIn(p.scopeId, 'editor'),
      onDragStart: (e: React.DragEvent) => onCardDragStart(e, k),
      onDragEnd: dragEnd,
      // Click selects (the links still open on touch, middle-click and Enter); double-click opens.
      // Capture phase: the links would navigate before a bubbling handler could stop them.
      onClickCapture: (e: React.MouseEvent) =>
        !(e.target as HTMLElement).closest('button') && onItemClick(e, k, () => !(e.target as HTMLElement).closest('a') && navigate(`/p/${p.id}`)),
      onDoubleClick: () => navigate(`/p/${p.id}`),
      onKeyDown: (e: React.KeyboardEvent) => e.key === 'Enter' && e.target === e.currentTarget && navigate(`/p/${p.id}`),
      onContextMenu: (e: React.MouseEvent) => {
        onItemContextMenu(k);
        openContextMenu(e, projectContext(p));
      },
      'data-project-card': p.id,
      'data-sel-key': k,
      'data-selected': sel.has(k) || undefined,
    };
  };
  const actionsMenu = (items: (MenuItem | 'sep')[], label: string) => (
    <span onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <Menu
        trigger={
          <Button variant="ghost" size="icon-sm" aria-label={label}>
            <MoreHorizontal size={15} />
          </Button>
        }
        items={items}
      />
    </span>
  );
  const editorsOf = (p: ProjectRow) => [...new Map(presence.filter((x) => x.projectId === p.id).map((x) => [x.userId, x])).values()];
  const editorsBadge = (p: ProjectRow) => {
    const uniq = editorsOf(p);
    return uniq.length ? (
      <div className="flex shrink-0 -space-x-1.5">
        {uniq.slice(0, 4).map((u) => (
          <span key={u.userId} className="shrink-0" title={`${u.name} is editing`}>
            <Avatar name={u.name} url={u.avatarUrl} size={18} color={u.color} />
          </span>
        ))}
      </div>
    ) : null;
  };
  const anySel = sel.size > 0;
  const check = (k: string, label: string, className?: string) => (
    <SelectCheck checked={sel.has(k)} show={anySel} label={label} onChange={() => toggle(k)} className={className} />
  );
  const whereOf = (p: ProjectRow) => (!inSpace && (p.spaceId || p.folderId) ? [sp.spaceById.get(p.spaceId)?.name, sp.folderById.get(p.folderId)?.name].filter(Boolean).join(' / ') : '');
  const useButton = (p: ProjectRow) =>
    filter === 'templates' && canUseTemplate(p) ? (
      <Button size="sm" variant="primary" onClick={(e) => (e.stopPropagation(), useTemplate(p))} onDoubleClick={(e) => e.stopPropagation()} data-use-template={p.id}>
        Use
      </Button>
    ) : null;

  const crumbs = inSpace ? sp.folderPath(folderId) : [];
  const title = !inSpace ? 'Projects' : folder ? folder.name : (space?.name ?? 'General');
  const placeTarget = folder
    ? { kind: 'folder' as const, id: folder.id, name: folder.name, restricted: folder.restricted }
    : space
      ? { kind: 'space' as const, id: space.id, name: space.name, restricted: space.restricted }
      : null;

  const dropCls = 'rounded data-[drop-target]:bg-accent/20 data-[drop-target]:text-fg data-[drop-target]:ring-1 data-[drop-target]:ring-accent';
  return (
    <div {...boxHandlers} onPointerCancel={boxHandlers.onPointerUp} className={cx('flex min-h-[70vh] items-start gap-6 pb-20', boxing && 'select-none')}>
      <div className="min-w-0 flex-1">
      {inSpace ? (
        <nav className="mb-2 flex flex-wrap items-center gap-1 text-sm text-muted" aria-label="Breadcrumb" data-breadcrumb>
          <Link to={`/orgs/${org.id}`} className="hover:text-fg">
            Projects
          </Link>
          <ChevronRight size={14} />
          <Link to={placeUrl(org.id, spaceId)} className={cx('flex items-center gap-1.5 px-1 hover:text-fg', dropCls)} data-crumb="" {...dropTarget('crumb:', () => ({ spaceId, folderId: '' }))}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: space?.color ?? '#64748b' }} />
            {space?.name ?? 'General'}
          </Link>
          {crumbs.map((f) => (
            <span key={f.id} className="flex items-center gap-1">
              <ChevronRight size={14} />
              <Link to={placeUrl(org.id, spaceId, f.id)} className={cx('px-1 hover:text-fg', dropCls)} data-crumb={f.id} {...dropTarget(`crumb:${f.id}`, () => ({ spaceId, folderId: f.id }))}>
                {f.name}
              </Link>
            </span>
          ))}
        </nav>
      ) : null}
      <PageHeader
        title={
          <span className="flex items-center gap-2" data-place-title>
            {title}
            {(folder ?? space)?.restricted ? (
              <span title="Restricted: only the people it is shared with, and admins">
                <Lock size={16} className="text-faint" />
              </span>
            ) : null}
          </span>
        }
        subtitle={inSpace ? (space?.description || org.name) : org.name}
        actions={
          <div className="flex flex-wrap gap-2">
            {placeTarget && sp.canIn(here, 'editor') && sp.role !== 'guest' ? (
              <Button onClick={() => setAccess(placeTarget)} data-open-access>
                {placeTarget.restricted ? <Lock size={15} /> : <Users size={15} />} Access
              </Button>
            ) : null}
            {space && sp.canIn(space.scopeId, 'editor') && !folder ? (
              <Menu
                trigger={
                  <Button variant="ghost" size="icon" aria-label="Space actions">
                    <MoreHorizontal size={16} />
                  </Button>
                }
                items={[
                  { label: 'Edit space', icon: <Pencil size={14} />, onClick: () => setEditSpace(space) },
                  ...(sp.canIn(space.scopeId, 'admin')
                    ? ([
                        'sep',
                        {
                          label: 'Delete space',
                          icon: <Trash2 size={14} />,
                          danger: true,
                          onClick: () => void attempt(() => reducers().deleteSpace({ id: space.id }), 'Space deleted').then((ok) => ok && navigate(`/orgs/${org.id}`)),
                        },
                      ] as const)
                    : []),
                ]}
              />
            ) : null}
            {!inSpace && can('editor') ? (
              <Button onClick={() => setNewSpace(true)} data-new-space>
                <Plus size={16} /> New space
              </Button>
            ) : null}
            {inSpace && canEditHere ? (
              <Button onClick={() => setNewFolder(true)} data-new-folder>
                <FolderPlus size={16} /> New folder
              </Button>
            ) : null}
            {canEditHere ? <PremiereImportButton orgId={org.id} spaceId={spaceId} folderId={folderId} onCreated={(id) => navigate(`/p/${id}`)} /> : null}
            {canEditHere ? (
              <Button variant="primary" onClick={() => setCreating(true)} data-new-project>
                <Plus size={16} /> New project
              </Button>
            ) : null}
          </div>
        }
      />

      {!inSpace ? (
        <div className="mb-8">
          <h2 className="mb-3 text-sm font-semibold text-muted">Spaces</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3" data-spaces>
            {can('viewer') ? (
              <SpaceCard to={placeUrl(org.id, '')} name="General" color="#64748b" count={projects.filter((p) => !p.spaceId && !p.archived).length} drop={dropTarget('space:', () => ({ spaceId: '', folderId: '' }))} />
            ) : null}
            {sp.spaces.map((s) => (
              <SpaceCard key={s.id} to={placeUrl(org.id, s.id)} name={s.name} color={s.color} restricted={s.restricted} count={projects.filter((p) => p.spaceId === s.id && !p.archived).length} id={s.id} drop={dropTarget(`space:${s.id}`, () => ({ spaceId: s.id, folderId: '' }))} />
            ))}
            {sharedFolders.map((f) => (
              <Link
                key={f.id}
                to={placeUrl(org.id, f.spaceId, f.id)}
                className="flex items-center gap-3 rounded-xl border border-line bg-panel p-3 hover:border-line-2 data-[drop-target]:border-accent data-[drop-target]:bg-accent/10"
                data-shared-folder={f.id}
                {...dropTarget(`shared:${f.id}`, () => ({ spaceId: f.spaceId, folderId: f.id }))}
              >
                <Folder size={18} className="text-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{f.name}</span>
                  <span className="block text-xs text-faint">Shared with you</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
          <Input className="pl-9" placeholder={inSpace ? 'Search this space' : 'Search all projects'} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <Tabs
          value={filter}
          onChange={setFilter}
          tabs={[
            { id: 'active', label: inSpace ? 'Active' : 'Recent' },
            { id: 'templates', label: 'Templates' },
            { id: 'archived', label: 'Archived' },
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <ViewToggle value={view} onChange={setView} />
          <InspectorToggle open={inspector.open} onClick={inspector.toggle} />
        </div>
      </div>

      {visible.length === 0 && !subfolders.length ? (
        <EmptyState
          icon={<Clapperboard size={32} />}
          title={filter === 'archived' ? 'No archived projects' : filter === 'templates' ? 'No templates yet' : inSpace ? 'Nothing here yet' : 'No projects yet'}
          action={
            canEditHere && filter === 'active' ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Plus size={16} /> Create a project
              </Button>
            ) : null
          }
        >
          {filter === 'active'
            ? inSpace
              ? 'Create a project or a folder here, or move projects in from their menu.'
              : 'Create a project to start editing together.'
            : filter === 'templates'
              ? 'Turn a project into a template from its menu. Name the clips that change (Inspector → Template field) to batch-export versions of it.'
              : null}
        </EmptyState>
      ) : view === 'table' ? (
        <div ref={gridRef} className={tableCls.wrap}>
          <table className={tableCls.table} data-project-table>
            <thead>
              <tr className={tableCls.head}>
                <th className="w-9 py-2 pl-3 pr-0 align-middle">
                  <SelectCheck checked={order.length > 0 && sel.size === order.length} mixed={sel.size > 0 && sel.size < order.length} show label="Select all" onChange={setAll} />
                </th>
                <SortTh k="name" sort={sort} onSort={onSort} first="asc" className="w-full">
                  Name
                </SortTh>
                <SortTh k="format" sort={sort} onSort={onSort} className="hidden sm:table-cell">
                  Format
                </SortTh>
                <SortTh k="fps" sort={sort} onSort={onSort} align="right" className="hidden lg:table-cell">
                  FPS
                </SortTh>
                <SortTh k="duration" sort={sort} onSort={onSort} align="right" className="hidden md:table-cell">
                  Length
                </SortTh>
                <SortTh k="size" sort={sort} onSort={onSort} align="right" className="hidden sm:table-cell">
                  Size
                </SortTh>
                <SortTh k="review" sort={sort} onSort={onSort} first="asc" className="hidden lg:table-cell">
                  Review
                </SortTh>
                <SortTh k="createdBy" sort={sort} onSort={onSort} first="asc" className="hidden xl:table-cell">
                  Created by
                </SortTh>
                <SortTh k="modified" sort={sort} onSort={onSort}>
                  Last edited
                </SortTh>
                <th className="w-10 px-1" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {subfolders.map((f) => {
                const s = folderStats(f.id);
                return (
                  <tr key={f.id} {...folderProps(f)} className={cx(tableCls.row, rowTone(sel.has(`f:${f.id}`), dropOn === f.id))}>
                    <td className="w-9 py-1.5 pl-3 pr-0">{check(`f:${f.id}`, `Select ${f.name}`)}</td>
                    <td className="max-w-0 px-3 py-1.5">
                      <div className="flex items-center gap-3">
                        <div className="flex aspect-video w-16 shrink-0 items-center justify-center rounded-md bg-panel-2 text-accent-2">
                          <Folder size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{f.name}</span>
                            {f.restricted ? <Lock size={12} className="shrink-0 text-faint" /> : null}
                          </div>
                          <div className="text-xs text-faint">{plural(s.active, 'project')}</div>
                        </div>
                      </div>
                    </td>
                    <td className={cx(tableCls.cell, 'hidden sm:table-cell')}>Folder</td>
                    <td className={cx(tableCls.cell, 'hidden lg:table-cell')} />
                    <td className={cx(tableCls.cell, 'hidden md:table-cell')} />
                    <td className={cx(tableCls.cell, 'hidden text-right sm:table-cell')}>{formatBytes(s.size)}</td>
                    <td className={cx(tableCls.cell, 'hidden lg:table-cell')} />
                    <td className={cx(tableCls.cell, 'hidden xl:table-cell')}>{f.createdByName || '—'}</td>
                    <td className={tableCls.cell}>
                      {s.latest ? (
                        <>
                          {timeAgo(s.latest.updatedAt)}
                          <span className="hidden text-faint xl:inline"> · {s.latest.updatedByName}</span>
                        </>
                      ) : (
                        timeAgo(f.createdAt)
                      )}
                    </td>
                    <td className="px-1 py-1.5">{actionsMenu(folderMenu(f), 'Folder actions')}</td>
                  </tr>
                );
              })}
              {visible.map((p) => {
                const review = REVIEW_LABEL[p.reviewStatus];
                const where = whereOf(p);
                return (
                  <tr key={p.id} tabIndex={0} {...projectProps(p)} className={cx(tableCls.row, rowTone(sel.has(`p:${p.id}`), false))}>
                    <td className="w-9 py-1.5 pl-3 pr-0">{check(`p:${p.id}`, `Select ${p.name}`)}</td>
                    <td className="max-w-0 px-3 py-1.5">
                      <div className="flex items-center gap-3">
                        <Link to={`/p/${p.id}`} className="relative block w-16 shrink-0" draggable={false} tabIndex={-1}>
                          <ProjectThumb project={p} assets={assetMap} className="rounded-md" duration={false} />
                          {logos.get(p.id) ? <img src={logos.get(p.id)} alt="" className="absolute bottom-0.5 left-0.5 h-4 w-4 shrink-0 rounded object-cover ring-1 ring-black/30" /> : null}
                        </Link>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Link to={`/p/${p.id}`} className="truncate font-medium hover:underline" draggable={false}>
                              {p.name}
                            </Link>
                            {p.scopeId ? <Lock size={12} className="shrink-0 text-faint" /> : null}
                            {p.template ? <Badge tone="accent">Template</Badge> : null}
                            {editorsBadge(p)}
                          </div>
                          <div className="truncate text-xs text-faint">
                            {where || <span className="sm:hidden">{timeAgo(p.updatedAt)}</span>}
                            {review ? <span className="lg:hidden">{where ? ' · ' : ''}{review.label}</span> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={cx(tableCls.cell, 'hidden sm:table-cell')}>
                      {p.width}×{p.height} <span className="text-faint">{aspectLabel(p.width, p.height)}</span>
                    </td>
                    <td className={cx(tableCls.cell, 'hidden text-right lg:table-cell')}>{p.fps}</td>
                    <td className={cx(tableCls.cell, 'hidden text-right font-mono text-xs md:table-cell')}>{formatDuration(p.durationUs)}</td>
                    <td className={cx(tableCls.cell, 'hidden text-right sm:table-cell')}>{formatBytes(index.sizeOf(index.mediaOf(p)))}</td>
                    <td className={cx(tableCls.cell, 'hidden lg:table-cell')}>{review ? <Badge tone={review.tone}>{review.label}</Badge> : <span className="text-faint">—</span>}</td>
                    <td className={cx(tableCls.cell, 'hidden xl:table-cell')}>{p.createdByName || '—'}</td>
                    <td className={tableCls.cell}>
                      {timeAgo(p.updatedAt)}
                      {p.updatedByName ? <span className="hidden text-faint xl:inline"> · {p.updatedByName}</span> : null}
                    </td>
                    <td className="px-1 py-1.5">
                      <div className="flex items-center justify-end gap-1">
                        {useButton(p)}
                        {sp.canIn(p.scopeId, 'editor') ? actionsMenu(projectMenu(p), 'Project actions') : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
      <div ref={gridRef}>
      {subfolders.length ? (
        <div className="mb-6 grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3" data-folders>
          {subfolders.map((f) => (
            <div
              key={f.id}
              role="link"
              {...folderProps(f)}
              className={cx(
                'group flex cursor-pointer select-none items-center gap-3 rounded-xl border bg-panel p-3 transition-colors',
                dropOn === f.id ? 'border-accent bg-accent/10 ring-2 ring-accent' : sel.has(`f:${f.id}`) ? 'border-accent ring-2 ring-accent/60' : 'border-line hover:border-line-2',
              )}
            >
              {check(`f:${f.id}`, `Select ${f.name}`)}
              <Folder size={20} className="shrink-0 text-accent-2" />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 truncate text-sm font-medium">
                  {f.name} {f.restricted ? <Lock size={12} className="shrink-0 text-faint" /> : null}
                </span>
                <span className="block text-xs text-faint">{plural(folderStats(f.id).active, 'project')}</span>
              </span>
              {actionsMenu(folderMenu(f), 'Folder actions')}
            </div>
          ))}
        </div>
      ) : null}
      {visible.length ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5">
          {visible.map((p) => {
            const review = REVIEW_LABEL[p.reviewStatus];
            const where = whereOf(p);
            const k = `p:${p.id}`;
            return (
              <div
                key={p.id}
                {...projectProps(p)}
                className={cx('group relative select-none rounded-2xl border bg-panel transition-colors', sel.has(k) ? 'border-accent ring-2 ring-accent/60' : 'border-line hover:border-line-2')}
              >
                <Link to={`/p/${p.id}`} className="relative block" draggable={false}>
                  <ProjectThumb project={p} assets={assetMap} />
                  {logos.get(p.id) ? (
                    <img src={logos.get(p.id)} alt="" className="absolute bottom-2 left-2 h-9 w-9 shrink-0 rounded-lg object-cover shadow-lg ring-1 ring-black/30" />
                  ) : null}
                  {p.scopeId ? (
                    <span className="absolute right-2 top-2 rounded-md bg-black/60 p-1 text-white" title="Restricted">
                      <Lock size={12} />
                    </span>
                  ) : null}
                </Link>
                {check(k, `Select ${p.name}`, 'absolute left-2.5 top-2.5 z-10 shadow')}
                <div className="flex items-start gap-2 p-3.5">
                  <Link to={`/p/${p.id}`} className="min-w-0 flex-1" draggable={false}>
                    <div className="truncate font-medium">{p.name}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-faint">
                      <span>
                        {p.width}×{p.height} · {p.fps}fps
                      </span>
                      <span>·</span>
                      <span>{timeAgo(p.updatedAt)}</span>
                    </div>
                    {where ? <div className="mt-0.5 truncate text-[11px] text-faint">{where}</div> : null}
                    <div className="mt-2 flex items-center gap-2">
                      {p.template ? <Badge tone="accent">Template</Badge> : null}
                      {review ? <Badge tone={review.tone}>{review.label}</Badge> : null}
                      {editorsBadge(p)}
                    </div>
                  </Link>
                  {useButton(p)}
                  {sp.canIn(p.scopeId, 'editor') ? actionsMenu(projectMenu(p), 'Project actions') : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      </div>
      )}
      </div>

      <InspectorPanel open={inspector.open} wide={inspector.wide} onClose={() => inspector.setOpen(false)}>
        <ProjectsInspector
          selected={selected}
          projects={projects}
          projectById={projectById}
          sp={sp}
          index={index}
          inSpace={inSpace}
          spaceId={spaceId}
          folderId={folderId}
          orgName={org.name}
        />
      </InspectorPanel>
      {boxElement}
      {selected.length ? (
        <div
          data-selection-bar
          className="fixed bottom-5 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded-xl border border-line-2 bg-panel-2 px-2 py-1.5 text-sm shadow-xl"
        >
          <span className="px-2 font-medium">{plural(selected.length, 'item')} selected</span>
          {selected.every(canEditKey) ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setMoving(selected)} data-move-selected>
                <FolderInput size={14} /> Move…
              </Button>
              {selected.some((k) => k.startsWith('p:')) ? (
                filter === 'archived' ? (
                  <Button size="sm" variant="ghost" onClick={() => void setArchived(selected, false)}>
                    <ArchiveRestore size={14} /> Restore
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => void setArchived(selected, true)} data-archive-selected>
                    <Archive size={14} /> Archive
                  </Button>
                )
              ) : null}
            </>
          ) : null}
          {selected.every(canDeleteKey) ? (
            <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleting(selected)}>
              <Trash2 size={14} /> Delete
            </Button>
          ) : null}
          <Button size="icon-sm" variant="ghost" aria-label="Clear selection" onClick={clear}>
            <X size={14} />
          </Button>
        </div>
      ) : null}
      <Modal open={!!logoFor} onClose={() => setLogoFor(null)} title={`Logo · ${logoFor?.name ?? ''}`} width={380}>
        {logoFor ? (
          <div className="flex items-center gap-4">
            <ImagePicker
              url={logos.get(logoFor.id) ?? ''}
              endpoint={`/projects/${logoFor.id}/logo`}
              maxPx={LOGO_SIZE}
              size={88}
              label="logo"
              fallback={<Clapperboard size={30} className="text-faint" />}
              onChange={(url) => attempt(() => reducers().setProjectLogo({ projectId: logoFor.id, url }), url ? 'Logo updated' : 'Logo removed')}
            />
            <p className="text-sm text-muted">Click or drop a square image. It is shown on the project card and in the editor.</p>
          </div>
        ) : null}
      </Modal>
      <NewProjectModal open={creating} orgId={org.id} spaceId={spaceId} folderId={folderId} onClose={() => setCreating(false)} onCreated={(id) => navigate(`/p/${id}`)} />
      <RenameModal project={renaming} onClose={() => setRenaming(null)} />
      <SpaceDialog orgId={org.id} open={newSpace} onClose={() => setNewSpace(false)} onCreated={(id) => navigate(placeUrl(org.id, id))} />
      <SpaceDialog orgId={org.id} space={editSpace} open={!!editSpace} onClose={() => setEditSpace(null)} />
      <NameDialog
        open={newFolder}
        title="New folder"
        action="Create"
        onClose={() => setNewFolder(false)}
        onSave={(name) => attempt(() => reducers().createProjectFolder({ id: uuidv7(), orgId: org.id, spaceId, parentId: folderId, name, restricted: false }), 'Folder created')}
      />
      <NameDialog
        open={!!renamingFolder}
        title="Rename folder"
        action="Save"
        initial={renamingFolder?.name ?? ''}
        onClose={() => setRenamingFolder(null)}
        onSave={(name) => attempt(() => reducers().renameProjectFolder({ id: renamingFolder!.id, name }))}
      />
      {moving ? (
        <MoveDialog
          orgId={org.id}
          open
          title={moving.length > 1 ? `Move ${plural(moving.length, 'item')}` : `Move “${(moving[0].startsWith('f:') ? sp.folderById.get(moving[0].slice(2))?.name : projectById.get(moving[0].slice(2))?.name) ?? ''}”`}
          current={
            moving.length === 1 && moving[0].startsWith('p:')
              ? { spaceId: projectById.get(moving[0].slice(2))?.spaceId ?? spaceId, folderId: projectById.get(moving[0].slice(2))?.folderId ?? '' }
              : moving.length === 1
                ? { spaceId: sp.folderById.get(moving[0].slice(2))?.spaceId ?? spaceId, folderId: sp.folderById.get(moving[0].slice(2))?.parentId ?? '' }
                : { spaceId, folderId }
          }
          excludeFolders={moving.filter((k) => k.startsWith('f:')).map((k) => k.slice(2))}
          onClose={() => setMoving(null)}
          onMove={(s, f) => moveTo(moving, { spaceId: s, folderId: f })}
        />
      ) : null}
      {access ? (
        <AccessDialog
          orgId={org.id}
          {...access}
          restricted={
            access.kind === 'space' ? (sp.spaceById.get(access.id)?.restricted ?? access.restricted) : (sp.folderById.get(access.id)?.restricted ?? access.restricted)
          }
          open
          onClose={() => setAccess(null)}
        />
      ) : null}
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={deleting && deleting.length > 1 ? `Delete ${plural(deleting.length, 'item')}` : deleting?.[0]?.startsWith('f:') ? 'Delete folder' : 'Delete project'}
        danger
        confirmLabel="Delete"
        requireText={
          deleting?.some((k) => k.startsWith('p:')) ? (deleting.length === 1 ? projectById.get(deleting[0].slice(2))?.name : `delete ${plural(deleting.length, 'item')}`) : undefined
        }
        message={
          <div className="space-y-2">
            {deleting?.some((k) => k.startsWith('p:')) ? <p>The timelines, comments and versions are deleted. Assets stay in the library.</p> : null}
            {deleting?.some((k) => k.startsWith('f:')) ? <p>Folders go away; their projects and subfolders move up one level.</p> : null}
          </div>
        }
        onConfirm={() =>
          attempt(async () => {
            for (const k of deleting ?? []) {
              if (k.startsWith('p:')) await reducers().deleteProject({ projectId: k.slice(2) });
              else await reducers().deleteProjectFolder({ id: k.slice(2) });
            }
          }, deleting && deleting.length > 1 ? `Deleted ${plural(deleting.length, 'item')}` : 'Deleted').then((ok) => ok && clear())
        }
      />
    </div>
  );
}

function SpaceCard({ to, name, color, restricted, count, id, drop }: { to: string; name: string; color: string; restricted?: boolean; count: number; id?: string; drop?: object }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl border border-line bg-panel p-3 transition-colors hover:border-line-2 data-[drop-target]:border-accent data-[drop-target]:bg-accent/10"
      data-space-card={id ?? 'general'}
      {...drop}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-white" style={{ background: color }}>
        {name.slice(0, 1).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 truncate text-sm font-medium">
          {name} {restricted ? <Lock size={12} className="shrink-0 text-faint" /> : null}
        </span>
        <span className="block text-xs text-faint">
          {count} project{count === 1 ? '' : 's'}
        </span>
      </span>
    </Link>
  );
}

function NewProjectModal({
  open,
  orgId,
  spaceId,
  folderId,
  onClose,
  onCreated,
}: {
  open: boolean;
  orgId: string;
  spaceId: string;
  folderId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('Untitled project');
  const [preset, setPreset] = useState(0);
  const [fps, setFps] = useState(30);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    const id = uuidv7();
    const p = PROJECT_PRESETS[preset];
    const ok = await attempt(() => reducers().createProject({ id, orgId, name, width: p.width, height: p.height, fps, spaceId, folderId }));
    setBusy(false);
    if (ok) {
      onClose();
      onCreated(id);
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={create}>
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && create()} />
        </Field>
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <Field label="Format">
            <Select className="w-full" value={preset} onChange={(e) => setPreset(Number(e.target.value))}>
              {PROJECT_PRESETS.map((p, i) => (
                <option key={p.label} value={i}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Frame rate">
            <Select className="w-full" value={fps} onChange={(e) => setFps(Number(e.target.value))}>
              {FPS_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f} fps
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function RenameModal({ project, onClose }: { project: ProjectRow | null; onClose: () => void }) {
  const [name, setName] = useState('');
  useEffect(() => setName(project?.name ?? ''), [project]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const ok = await attempt(() => reducers().updateProject({ projectId: project!.id, name } as any));
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal
      open={!!project}
      onClose={onClose}
      title="Rename project"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && save()} />
    </Modal>
  );
}
